import { etagFromRow, get, getVersioned, post, seg } from '../client';
import type { ListParams, Page, PriorityLevel } from '../types/common';
import type {
  CareTeamMember, CoverageDelegation, CoverageList, CreateCareTeamMemberRequest, CreateCoverageDelegationRequest, CreateReferralRequest, CreateTaskRequest,
  InboxCategory, InboxCounts, InboxItem, InboxScope, Referral, ReferralDisposition, Task, TaskAssignee, TaskEvent, TaskListParams,
} from '../types/work';

/**
 * Work coordination (tasks, inbox, referrals, care team, coverage) against core-api care-coordination.
 *
 * Every command on an existing aggregate needs If-Match (the aggregate's ETag, or `W/"<rowVersion>"` from a list
 * item) and an Idempotency-Key; commands return the new state with its new ETag so callers can chain.
 */

/** "all" (or empty) is a UI sentinel meaning no filter; the backend treats status literally (R-037). */
export const workStatusParam = (status?: string | null) => (!status || status === 'all' ? undefined : status);

/** If-Match for a row from a list: prefer an explicit ETag, else derive it from rowVersion/version. */
export const workEtag = (row: { rowVersion?: number | null; version?: number | null } | null | undefined): string | null =>
  row ? etagFromRow(row.rowVersion ?? row.version ?? null) : null;

const tcmd = (id: string, c: string) => `/tasks/${seg(id)}:${c}`;

export const tasksApi = {
  list: (p: TaskListParams = {}) => get<Page<Task>>('/tasks', { ...p }),
  create: (body: CreateTaskRequest) => post<Task>('/tasks', body, { idempotent: true }),
  get: (id: string) => getVersioned<Task>(`/tasks/${seg(id)}`),
  claim: (id: string, etag: string | null) => post<Task>(tcmd(id, 'claim'), {}, { ifMatch: etag, idempotent: true }),
  assign: (id: string, assigneeId: string, etag: string | null) => post<Task>(tcmd(id, 'assign'), { assigneeId }, { ifMatch: etag, idempotent: true }),
  start: (id: string, etag: string | null) => post<Task>(tcmd(id, 'start'), {}, { ifMatch: etag, idempotent: true }),
  complete: (id: string, resolution: string, etag: string | null) => post<Task>(tcmd(id, 'complete'), { resolution }, { ifMatch: etag, idempotent: true }),
  reopen: (id: string, reason: string, etag: string | null) => post<Task>(tcmd(id, 'reopen'), { reason }, { ifMatch: etag, idempotent: true }),
  escalate: (id: string, reason: string, etag: string | null) => post<Task>(tcmd(id, 'escalate'), { reason }, { ifMatch: etag, idempotent: true }),
  /** Comments bump the task version too: the response ETag is the task's new ETag. */
  comment: (id: string, text: string, etag?: string | null) => post<TaskEvent>(tcmd(id, 'comment'), { text }, { ifMatch: etag ?? null, idempotent: true }),
  events: (id: string) => get<Page<TaskEvent>>(`/tasks/${seg(id)}/events`),
  /** Workforce members in my tenant who can own, accept or witness work (assignment, handoff, witnessed waste). */
  assignees: () => get<Page<TaskAssignee>>('/tasks/assignees'),
};

export const inboxApi = {
  list: (p: ListParams & { category?: InboxCategory; priority?: PriorityLevel; scope?: 'mine' | 'pool' | 'covering' } = {}) =>
    get<Page<InboxItem>>('/inbox', { limit: 100, ...p }),
  counts: () => get<InboxCounts>('/inbox/counts'),
  /** Counts for one scope (mine | pool | covering); `byScope` carries all three totals. */
  countsFor: (scope: InboxScope) => get<InboxCounts>('/inbox/counts', { scope }),
  /**
   * POST /inbox:bulk — assign plain (non-critical, non-source-owned) tasks of one type to a person in one
   * transaction. Each item carries its own If-Match (`W/"<version>"` of the inbox item).
   */
  bulkAssign: (assigneeId: string, items: { id: string; ifMatch: string }[]) =>
    post<Page<Task>>('/inbox:bulk', { action: 'assign', assigneeId, items }, { idempotent: true }),
};

const rfcmd = (id: string, c: string) => `/referrals/${seg(id)}:${c}`;
/** Referral commands return the new referral; chain with `workEtag(result)`. */
const rf = (id: string, c: string, body: object, etag: string | null) => post<Referral>(rfcmd(id, c), body, { ifMatch: etag, idempotent: true });

/** Referral closed loop: draft → ready → sent → received → scheduled → consult_received → reviewed → closed. */
export const referralsApi = {
  list: (p: ListParams & { status?: string; patientId?: string } = {}) => get<Page<Referral>>('/referrals', { ...p, status: workStatusParam(p.status) }),
  get: (id: string) => getVersioned<Referral>(`/referrals/${seg(id)}`),
  create: (body: CreateReferralRequest) => post<Referral>('/referrals', body, { idempotent: true }),
  /** Draft → ready (all required fields complete). */
  ready: (id: string, etag: string | null) => rf(id, 'ready', {}, etag),
  send: (id: string, etag: string | null) => rf(id, 'send', {}, etag),
  receive: (id: string, etag: string | null) => rf(id, 'receive', {}, etag),
  schedule: (id: string, note: string, etag: string | null) => rf(id, 'schedule', note ? { note } : {}, etag),
  recordConsult: (id: string, consultNote: string, etag: string | null) => rf(id, 'record-consult', { consultNote }, etag),
  /** Consult received → reviewed (the reviewing clinician's note is optional). */
  review: (id: string, note: string, etag: string | null) => rf(id, 'review', note ? { note } : {}, etag),
  /** Reviewed (consult on file) or a documented disposition → closed. */
  close: (id: string, reason: string, etag: string | null) => rf(id, 'close', { reason }, etag),
  cancel: (id: string, reason: string, etag: string | null) => rf(id, 'cancel', { reason }, etag),
  /** Documented disposition (denied, declined, unable_to_contact, duplicate, expired); the referral can then be closed. */
  disposition: (id: string, kind: ReferralDisposition, reason: string, etag: string | null) => rf(id, kind, { reason }, etag),
};

export const careTeamApi = {
  proposals: (p: { cursor?: string } = {}) => get<Page<CareTeamMember & { patient: { id: string; display: string; dob: string; mrn: string } }>>('/care-team-proposals', p),
  list: (patientId: string) => get<Page<CareTeamMember>>(`/patients/${seg(patientId)}/care-team`),
  add: (patientId: string, body: CreateCareTeamMemberRequest) => post<CareTeamMember>(`/patients/${seg(patientId)}/care-team`, body, { idempotent: true }),
  /** `etag`: `W/"<rowVersion>"` of the membership (from the care-team list). */
  accept: (id: string, etag?: string | null) => post<CareTeamMember>(`/care-team/${seg(id)}:accept`, {}, { ifMatch: etag ?? null, idempotent: true }),
  end: (id: string, reason: string, etag?: string | null) => post<CareTeamMember>(`/care-team/${seg(id)}:end`, { reason }, { ifMatch: etag ?? null, idempotent: true }),
};

export const coverageApi = {
  list: () => get<CoverageList>('/coverage-delegations'),
  create: (body: CreateCoverageDelegationRequest) => post<CoverageDelegation>('/coverage-delegations', body, { idempotent: true }),
  end: (id: string, etag?: string | null) => post<CoverageDelegation>(`/coverage-delegations/${seg(id)}:end`, {}, { ifMatch: etag ?? null, idempotent: true }),
};
